import { useState } from 'react'

import getConfig from '@/config'
import {
  chain,
  path,
  either,
  isNil,
  isEmpty,
  map,
  pipe,
  pluck,
  prop,
  reject,
  uniq,
} from 'ramda'

import { isNativeiOSAppBrowser } from '../../lib/client-info'
import { useEventTracker } from '@/components/Tracker/ContentView'
import {
  runAuthenticatedGetRequest,
  runGetRequest,
  useAuthenticatedGetRequest,
  useAuthenticatedRequest,
  useGetRequest,
} from '../../modules/api/hooks'
import { APIHookResponse } from '../../modules/api/types'

import {
  ActiveSessionWithResponsesFragment,
  LearnerRatingQuestionsFragment,
  FinishedSessionWithResponsesFragment,
  LearnerSessionEventFragment,
  LibraryContentQuery,
  ModuleResponseAndCompletionFragment,
  SeriesWithModulesFragment,
  MinimalModuleListItemFragment,
} from './api-types'
import {
  LearningModuleType,
  LearningModuleWithSessionType,
  LearningSeriesType,
  LearningSeriesWithModulesType,
  LearnerModuleReflections,
} from './types'
import { McqSettingsPassMarkType } from './index'

const siteConfig = getConfig().publicRuntimeConfig

export const reportGraphQLError = (e?: Error) => {
  if (e) {
    console.error(e)
  }
}

export const useLearningLibrary = (
  libraryType: string,
  libraryID: string
): {
  data?: LibraryContentQuery & { languages: string[] }
  loading?: boolean
  error?: Error
} => {
  const disallow = useAreSubscriptionsDisallowed()
  return useAuthenticatedGetRequest(
    `/api/cms/learning/${libraryType}/${libraryID}`
  )
}

export const usePublicLearningLibrary = (
  libraryID: string
): {
  data?: LibraryContentQuery & { languages: string[] }
  loading?: boolean
  error?: Error
} => {
  const disallow = useAreSubscriptionsDisallowed()
  return useGetRequest(`/api/cms/learning/group/${libraryID}`)
}

export const getPublicLearningLibrary = (
  libraryID: string
): Promise<{
  data?: LibraryContentQuery & { languages: string[] }
  loading?: boolean
  error?: Error
}> => {
  const disallow = useAreSubscriptionsDisallowed()
  return runGetRequest(`/api/cms/learning/group/${libraryID}`)
}

export const useAreSubscriptionsDisallowed = () => {
  const isIOS = isNativeiOSAppBrowser()
  return isIOS
}

const useDisallowSubscriptions = () => {
  const disallow = useAreSubscriptionsDisallowed()
  return disallow ? { _eq: false } : {}
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const useSeries = (
  libraryType: string,
  libraryID: string,
  seriesID: string,
  userID?: string
): {
  data?: LearningSeriesWithModulesType
  loading?: boolean
  error?: Error
} => {
  const disallow = useAreSubscriptionsDisallowed()
  return userID
    ? useAuthenticatedGetRequest(
        `/api/cms/learning/${libraryType}/${libraryID}/${seriesID}?disallowSubscriptions=${
          disallow ? '1' : ''
        }`
      )
    : useGetRequest(
        `/api/cms/learning/${libraryType}/${libraryID}/${seriesID}?disallowSubscriptions=${
          disallow ? '1' : ''
        }`
      )
}

export const useSeriesByID = (
  seriesID: string,
  userID?: string
): {
  data?: LearningSeriesWithModulesType
  loading?: boolean
  error?: Error
} => {
  const disallow = useAreSubscriptionsDisallowed()
  return userID
    ? useAuthenticatedGetRequest(
        `/api/cms/learning/series/${seriesID}?disallowSubscriptions=${
          disallow ? '1' : ''
        }`
      )
    : useGetRequest(
        `/api/cms/learning/series/${seriesID}?disallowSubscriptions=${
          disallow ? '1' : ''
        }`
      )
}

export const getSeries = (
  libraryType: string,
  libraryID: string,
  seriesID: string,
  userID?: string,
  headers?: any
): Promise<{
  data?: SeriesWithModulesFragment
  loading?: boolean
  error?: Error
}> => {
  const disallow = useAreSubscriptionsDisallowed()
  return userID
    ? runAuthenticatedGetRequest(
        `/api/cms/learning/${libraryType}/${libraryID}/${seriesID}?disallowSubscriptions=${
          disallow ? '1' : ''
        }`,
        headers
      )
    : runGetRequest(
        `/api/cms/learning/${libraryType}/${libraryID}/${seriesID}?disallowSubscriptions=${
          disallow ? '1' : ''
        }`,
        headers
      )
}

const getModuleWithQueries = async function (
  preview: 'draft' | 'published' | 'learning_published',
  libraryType: string,
  libraryID: string,
  moduleID: string,
  seriesID: string,
  vars: any,
  headers?: any
): Promise<
  APIHookResponse<ModuleResponseAndCompletionFragment> & {
    exists?: boolean
  }
> {
  const disallow = useAreSubscriptionsDisallowed()
  const use = vars.userID ? runAuthenticatedGetRequest : runGetRequest
  const {
    data: moduleData,
    ...rest
  }: APIHookResponse<ModuleResponseAndCompletionFragment> = await use(
    `/api/cms/learning/${libraryType}/${libraryID}/${seriesID}/${moduleID}?disallowSubscriptions=${
      disallow ? '1' : ''
    }${preview === 'draft' ? '&preview=1' : ''}`,
    headers
  )
  if (moduleData) {
    return {
      data: moduleData.status ? moduleData : undefined,
      exists: rest.response.status !== 404,
      ...rest,
    }
  }
  return rest
}

const useModuleWithQueries = function (
  preview: 'draft' | 'published' | 'learning_published',
  libraryType: string,
  libraryID: string,
  moduleID: string,
  seriesID: string,
  vars: any
): APIHookResponse<LearningModuleWithSessionType> & {
  exists?: boolean
} {
  const disallow = useAreSubscriptionsDisallowed()
  const use = vars.userID ? useAuthenticatedGetRequest : useGetRequest

  const {
    data: moduleData,
    ...rest
  }: APIHookResponse<LearningModuleWithSessionType> = use(
    `/api/cms/learning/${libraryType}/${libraryID}/${seriesID}/${moduleID}?disallowSubscriptions=${
      disallow ? '1' : ''
    }${preview === 'draft' ? '&preview=1' : ''}`
  )
  if (moduleData) {
    return {
      data: moduleData.status ? moduleData : undefined,
      exists: rest.response.status !== 404,
      ...rest,
    }
  }
  return rest
}

export const getModule = (
  libraryType: string,
  libraryID: string,
  moduleID: string,
  seriesID: string,
  currentUser?: { id: string },
  headers?: any
) => {
  return getModuleWithQueries(
    'published',
    libraryType,
    libraryID,
    moduleID,
    seriesID,
    {
      userID: currentUser ? parseInt(currentUser.id) : undefined,
    },
    headers
  )
}

export const useModule = (
  libraryType: string,
  libraryID: string,
  moduleID: string,
  seriesID: string,
  currentUser?: { id: string }
) => {
  return useModuleWithQueries(
    'published',
    libraryType,
    libraryID,
    moduleID,
    seriesID,
    {
      userID: currentUser ? parseInt(currentUser.id) : undefined,
    }
  )
}

export const useLearningModule = (
  libraryType: string,
  libraryID: string,
  moduleID: string,
  seriesID: string,
  currentUser: { id: string }
) => {
  return useModuleWithQueries(
    'learning_published',
    libraryType,
    libraryID,
    moduleID,
    seriesID,
    {
      userID: currentUser ? parseInt(currentUser.id) : undefined,
    }
  )
}

export const useDraftModule = (
  libraryType: string,
  libraryID: string,
  moduleID: string,
  seriesID: string,
  currentUser?: { id: string }
) => {
  return useModuleWithQueries(
    'draft',
    libraryType,
    libraryID,
    moduleID,
    seriesID,
    {
      userID: currentUser ? parseInt(currentUser.id) : undefined,
    }
  )
}

export type LearnerSessionAnswer = {
  id: string // uuid of answer
  selected?: boolean // whether user selected this answer
}

export type LearnerSessionAnswers = LearnerSessionAnswer[]

type LearnerSession = ActiveSessionWithResponsesFragment

type LearnerSessionFinished = FinishedSessionWithResponsesFragment

type LearnerSessionQuestionFeedback = {
  question_id: string
  feedback: string
  answers: LearnerSessionAnswerFeedback[]
}

type LearnerSessionAnswerFeedback = {
  answer_id: string
  selected: boolean
  answered_correctly: boolean
}

type LearnerSessionFeedback = LearnerSessionQuestionFeedback[]

const getMaybeMostRecentEvent = sessions => {
  if (!sessions) return null

  const mostRecentSession = sessions[sessions.length - 1]
  return mostRecentSession?.events[mostRecentSession?.events.length - 1]
}

export type LastCompletedLearnerSessions = Readonly<{
  sessions?: FinishedSessionWithResponsesFragment[]
  loading: boolean
}>

export const useLastCompletedLearnerSession = (
  libraryType: string,
  libraryID: string,
  seriesID: string,
  moduleID: string,
  actorID: number
): LastCompletedLearnerSessions => {
  libraryType = 'group'
  const { data, loading } = useAuthenticatedGetRequest<
    FinishedSessionWithResponsesFragment[]
  >(
    `/api/profile/learning/${libraryType}/${libraryID}/series/${seriesID}/module/${moduleID}/${actorID}/certificate-session`
  )

  return {
    sessions: data,
    loading,
  }
}

export const useLastCompletedLearnerSessionForSeries = (
  libraryType: string,
  libraryID: string,
  seriesID: string,
  actorID: number
): LastCompletedLearnerSessions => {
  libraryType = 'group'
  const { data, loading } = useAuthenticatedGetRequest<
    FinishedSessionWithResponsesFragment[]
  >(
    `/api/profile/learning/${libraryType}/${libraryID}/series/${seriesID}/module/_/${actorID}/certificate-session`
  )

  return {
    sessions: data,
    loading: loading,
  }
}

export type LearnerSessionManager = Readonly<{
  id: () => number
  loading: () => boolean
  error: () => Error | undefined
  loadingSessions: () => boolean
  sessions: () => (FinishedSessionWithResponsesFragment & {
    events: LearnerSessionEventFragment[]
  })[]
  session: () => LearnerSession | LearnerSessionFinished | undefined
  start: () => Promise<LearnerSession>
  answer: (
    pageID: string,
    questionID: string,
    answers: LearnerSessionAnswers
  ) => Promise<LearnerSession>
  finish: () => Promise<LearnerSession>
}>

export const useLearnerSession = (
  moduleID: string,
  seriesID: string,
  actorID: number
): LearnerSessionManager => {
  const request = useAuthenticatedRequest()
  const [mutationState, setMutationState] = useState<{
    loading: boolean
    error?: Error
  }>({ loading: false })

  const {
    data: sessionsData,
    loading: loadingSessions,
    refresh: refreshSessions,
  } = useAuthenticatedGetRequest<{
    learning_learner_sessions: (FinishedSessionWithResponsesFragment & {
      events: LearnerSessionEventFragment[]
    })[]
  }>(`/api/learner/modules/${moduleID}/sessions`)

  const sessions = sessionsData?.learning_learner_sessions
  const session = sessions?.length
    ? getMaybeMostRecentEvent(sessions)?.session
    : undefined

  const track = useEventTracker()

  const executeMutation = async (
    method: string,
    url: string,
    json?: any
  ): Promise<any> => {
    if (!request) throw new Error('Not authenticated')
    setMutationState({ loading: true })
    try {
      const res = await request(method, url, undefined, json)
      setMutationState({ loading: false })
      refreshSessions?.()
      return res.payload
    } catch (err) {
      setMutationState({ loading: false, error: err as Error })
      throw err
    }
  }

  return {
    id: () => (sessionsData ? (sessions?.length ?? 0) : 0),
    loading: () => mutationState.loading,
    error: () => mutationState.error,
    loadingSessions: () => loadingSessions,
    sessions: () => sessions,
    session: () => session,
    start: () => {
      const maybeMostRecentEvent = getMaybeMostRecentEvent(sessions)
      const isFirstAttempt = !maybeMostRecentEvent
      const isRetrying =
        sessions?.length > 0 && !maybeMostRecentEvent?.session.active
      if (isFirstAttempt || isRetrying) {
        return executeMutation(
          'POST',
          `/api/learner/modules/${moduleID}/sessions/start`
        ).then(data => {
          const session = data?.event?.session
          track.event('Learning Module', 'Session Start', {
            label: moduleID,
            'Content ID': moduleID,
            'Content Title': session?.revision?.title,
          })
          return session
        })
      }
      return Promise.resolve(true) as any
    },
    answer: (
      pageID: string,
      questionID: string,
      answers: LearnerSessionAnswers
    ) =>
      executeMutation(
        'POST',
        `/api/learner/modules/${moduleID}/sessions/answer`,
        { pageID, questionID, answers }
      ).then(data => {
        const session = data?.event?.session
        track.event('Learning Module', 'Session Answer', {
          label: moduleID,
          'Content ID': moduleID,
          'Content Title': session?.revision?.title,
          item_uid: questionID,
          item_type: 'learning_question',
        })
        return session
      }),
    finish: async () =>
      executeMutation(
        'POST',
        `/api/learner/modules/${moduleID}/sessions/finish`
      ).then(data => {
        const session = data?.event?.session
        track.event('Learning Module', 'Session Finish', {
          label: moduleID,
          'Content ID': moduleID,
          'Content Title': session?.revision?.title,
        })
        return session
      }),
  }
}

export type LearnerReflectionsEditor = Readonly<{
  loading: boolean
  saving: boolean
  error?: Error
  reflections: Readonly<LearnerModuleReflections>
  update: (reflections: { body: string }) => Promise<LearnerModuleReflections>
  refetch: () => void
}>

export const useLearnerReflectionsEditor = (
  moduleID: string,
  actorID: number,
  isPreview?: boolean
): LearnerReflectionsEditor => {
  const { data, loading, error, refetch } = useLearnerReflections(
    moduleID,
    actorID
  )
  const request = useAuthenticatedRequest()
  const [updateState, setUpdateState] = useState<{
    loading: boolean
    error?: Error
  }>({ loading: false })

  reportGraphQLError(error)
  reportGraphQLError(updateState.error)

  return {
    loading,
    saving: updateState.loading,
    error: updateState.error ?? error,
    reflections: isPreview ? { body: '' } : data,
    refetch,
    update: reflections => {
      setUpdateState({ loading: true })
      return request(
        'PUT',
        `/api/learner/modules/${moduleID}/reflections`,
        undefined,
        {
          body: reflections.body,
        }
      )
        .then(res => {
          setUpdateState({ loading: false })
          return res.payload?.reflections
        })
        .catch(err => {
          setUpdateState({ loading: false, error: err as Error })
          throw err
        })
    },
  }
}

export const useLearnerReflections = (
  moduleID: string,
  actorID: number
): {
  data?: LearnerModuleReflections
  loading: boolean
  error?: Error
  refetch: () => void
} => {
  const { data, loading, error, refresh } = useAuthenticatedGetRequest<{
    reflections: LearnerModuleReflections
  }>(`/api/learner/modules/${moduleID}/reflections`)

  return {
    data: data ? data.reflections : undefined,
    loading,
    error,
    refetch: refresh ?? (() => {}),
  }
}

type LearnerRatingsEditor = Readonly<{
  loading: boolean
  saving: boolean
  error?: Error
  questions: Readonly<LearnerRatingQuestionsFragment[]>
  responses: any
  update: (
    responses: {
      question_id: string
      response?: number
      response_text?: string
    }[]
  ) => Promise<any>
}>

export const useLearnerRatingsEditor = (
  seriesID: string,
  moduleID: string,
  actorID: number,
  sessionID: string,
  isPreview?: boolean
): LearnerRatingsEditor => {
  const {
    data: questionsData,
    loading: questionsLoading,
    error: questionsError,
  } = useLearnerRatingsQuestions(seriesID, moduleID)

  const {
    data: responsesData,
    loading: responsesLoading,
    error: responsesError,
  } = useLearnerRatingsResponses(moduleID, actorID, sessionID)

  const request = useAuthenticatedRequest()
  const [updateState, setUpdateState] = useState<{
    loading: boolean
    error?: Error
  }>({ loading: false })

  reportGraphQLError(questionsError)
  reportGraphQLError(updateState.error)

  return {
    loading: questionsLoading,
    saving: updateState.loading,
    error: updateState.error ?? questionsError,
    questions: questionsData,
    responses: isPreview ? [] : responsesData,
    update: (
      responses: {
        question_id: string
        response?: number
        response_text?: string
      }[]
    ) => {
      setUpdateState({ loading: true })
      return request(
        'PUT',
        `/api/learner/modules/${moduleID}/ratings`,
        undefined,
        {
          sessionID,
          responses,
        }
      )
        .then(res => {
          setUpdateState({ loading: false })
          return res.payload
        })
        .catch(err => {
          setUpdateState({ loading: false, error: err as Error })
          throw err
        })
    },
  }
}

export const useLearnerRatingsQuestions = (
  seriesID: string,
  moduleID: string
): {
  data?: LearnerRatingQuestionsFragment[]
  loading: boolean
  error?: Error
} => {
  const { data, loading, error } = useAuthenticatedGetRequest<{
    module_questions: LearnerRatingQuestionsFragment[]
    series_questions: LearnerRatingQuestionsFragment[]
    standard_questions: LearnerRatingQuestionsFragment[]
  }>(`/api/learner/modules/${moduleID}/rating-questions?seriesID=${seriesID}`)

  return {
    data: data
      ? data.module_questions.length
        ? data.module_questions
        : data.series_questions.length
          ? data.series_questions
          : data.standard_questions
      : undefined,
    loading,
    error,
  }
}

const useLearnerRatingsResponses = (
  moduleID: string,
  userID: number,
  sessionID: string
) => {
  const { data, loading, error } = useAuthenticatedGetRequest<{
    learning_learner_module_rating_responses: any[]
  }>(`/api/learner/modules/${moduleID}/rating-responses?sessionID=${sessionID}`)

  return {
    data: data?.learning_learner_module_rating_responses,
    loading,
    error,
  }
}

export const useLearnerStarRatingsEditor = () => {
  const request = useAuthenticatedRequest()

  return {
    update: ({ moduleID, rating }: { moduleID: string; rating: number }) =>
      request(
        'PUT',
        `/api/learner/modules/${moduleID}/star-rating`,
        undefined,
        {
          rating,
        }
      ).then(x => console.log(x)),
  }
}

export const useNextIncompleteModuleInSeries = (
  module: LearningModuleWithSessionType
): {
  loading: boolean
  error?: Error
  previousModulesCompleted: boolean
  nextModule?: MinimalModuleListItemFragment
} => {
  const currentModulePosition = module.position

  const { loading, error, data } = useAuthenticatedGetRequest<{
    modules: MinimalModuleListItemFragment[]
  }>(`/api/learner/series/${module.series_id}/modules-completion`)

  const nextModule = data?.modules?.find(m =>
    module.revision?.mcq_settings_pass_mark_type ===
    McqSettingsPassMarkType.none
      ? !m.ever_completed
      : !m.has_passed
  )

  return {
    loading,
    error,
    previousModulesCompleted: nextModule?.position >= module.position,
    nextModule: nextModule,
  }
}

export const useModulesInSeriesPassed = seriesID => {
  const { loading, error, data } = useAuthenticatedGetRequest(
    '/api/cms/learning/series/' + seriesID + '/has-passed'
  )

  if (!loading && !error && data && data['data']) {
    return { loading: false, hasPassedAll: data['data']['hasPassedAll'] }
  }

  return { loading, error }
}
